import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Button,
  Box,
  Grid,
  Text,
  Flex,
  Input,
  Spinner,
  useToast,
  VStack,
  Switch,
  Select,
} from "@chakra-ui/react";
import { useState, useRef, useContext, useEffect } from "react";
import CustomInput from "components/common/customInput/CustomInput";
import FileViewer from "components/common/FileViewerPdfAndImage/FileViewer";
import { ViewIcon } from "@chakra-ui/icons";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";

const COMPANY_CODES = [
  "EBL",
  "EMX",
  "ENL",
  "ELPL",
  "ENZ",
  "EPC",
  "ESA",
  "EPE",
  "EPP",
  "EWPL",
  "EAU",
  "EPL",
  "EMN",
  "EPDR",
  "EUK",
  "EBT",
  "EMCT",
  "EUS",
  "AVL",
  "LPK",
  "MNPI",
  "MPI",
  "TII",
  "TIL",
  "TIM",
  "ZHL",
];

const MasterDataDrawer = ({
  getData,
  editData,
  isOpen,
  onClose,
}: {
  getData: any;
  editData?: any;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { workflowProcess } = useContext(AppContext);
  const toast = useToast();
  const isEditMode = !!editData;

  const [form, setForm] = useState<any>({
    companyCode: "",
    vendorName: "",
    vendorCode: "",
    validateFrom: "",
    validateTo: "",
    isActive: true,
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [vendorSearch, setVendorSearch] = useState("");
  const [vendorList, setVendorList] = useState<any[]>([]);
  const [showVendorList, setShowVendorList] = useState(false);
  const [isVendorLoading, setIsVendorLoading] = useState(false);
  const [isVendorSearching, setIsVendorSearching] = useState(false);
  const vendorTimerRef = useRef<NodeJS.Timeout | null>(null);
  const vendorContainerRef = useRef<HTMLDivElement>(null);

  const [documentName, setDocumentName] = useState("");
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [fetchFileLoader, setFetchFileLoader] = useState(false);

  const getFile = async (file: any) => {
    try {
      setFetchFileLoader(true);
      const response: any = await appRequest("filesystem", "get", {
        id: file._id,
      });
      if (response && !response.error) {
        let buffer =
          typeof response === "string" ? JSON.parse(response) : response;
        buffer = new Uint8Array(buffer);

        const fileURL = URL.createObjectURL(
          new Blob([buffer], { type: file.metadata.contentType }),
        );
        setDocumentUrl(fileURL);
      } else {
        toast({
          title: "Error",
          description: "Failed to load file",
          status: "error",
          isClosable: true,
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to fetch file",
        status: "error",
        isClosable: true,
      });
    } finally {
      setFetchFileLoader(false);
    }
  };

  // Load edit data when drawer opens
  useEffect(() => {
    if (isEditMode && editData) {
      const selectedCompany = COMPANY_CODES.find(
        (it: any) => it === editData.companyCode,
      );

      const formattedCompany = selectedCompany
        ? { label: selectedCompany, value: selectedCompany }
        : null;

      setForm({
        companyCode: formattedCompany,
        vendorName: editData.vendorName || "",
        vendorCode: editData.vendorCode || "",
        validateFrom: editData.validateFrom
          ? new Date(editData.validateFrom).toISOString().split("T")[0]
          : "",
        validateTo: editData.validateTo
          ? new Date(editData.validateTo).toISOString().split("T")[0]
          : "",
        isActive: editData.isActive !== false,
      });
      setDocumentName(editData.documentName || "");
      setVendorSearch(editData.vendorName || "");
      setDocumentFile(null); // Clear file for editing

      // Fetch the file if it exists
      if (editData.file && editData.file._id) {
        getFile(editData.file);
      }
    } else {
      // Reset form for create mode
      setForm({
        companyCode: "",
        vendorName: "",
        vendorCode: "",
        validateFrom: "",
        validateTo: "",
        isActive: true,
      });
      setDocumentName("");
      setVendorSearch("");
      setDocumentUrl(null);
      setDocumentFile(null);
    }
  }, [isOpen, editData, isEditMode]);

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setForm((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleCompanyChange = (e: any) => {
    setForm((prev: any) => ({
      ...prev,
      companyCode: e.target.value,
      vendorName: "",
      vendorCode: "",
    }));
    setVendorSearch("");
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        vendorContainerRef.current &&
        !vendorContainerRef.current.contains(event.target as Node)
      ) {
        setShowVendorList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleVendorSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setVendorSearch(value);
    setForm((prev: any) => ({ ...prev, vendorName: value, vendorCode: "" }));

    if (vendorTimerRef.current) clearTimeout(vendorTimerRef.current);

    if (!value || value.length < 2 || !form.companyCode) {
      setVendorList([]);
      setShowVendorList(false);
      return;
    }

    vendorTimerRef.current = setTimeout(async () => {
      setIsVendorLoading(true);
      try {
        const res: any = await appRequest("user", "masterFinancialApi", {
          type: "getVendorByName",
          vendorName: value,
          companyCode: form.companyCode?.value || form.companyCode,
          // ...workFlowData,
        });

        if (res?.status === "success" && Array.isArray(res.data)) {
          /*...*/
          const validVendors = res.data.filter(
            (v: any) => v.vendorCode && v.supplierName,
          );
          setVendorList(validVendors);
          setShowVendorList(validVendors.length > 0);
        } else {
          setVendorList([]);
          setShowVendorList(false);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsVendorLoading(false);
      }
    }, 600);
  };

  const selectVendor = (vendor: any) => {
    setForm((prev: any) => ({
      ...prev,
      vendorName: vendor.supplierName,
      vendorCode: vendor.vendorCode,
    }));
    setVendorSearch(vendor.supplierName);
    setShowVendorList(false);
  };

  const handleVendorSearch = async () => {
    if (!vendorSearch?.trim() || !form.companyCode) {
      toast({
        title: "Enter vendor name/code and select company",
        status: "warning",
        duration: 3000,
      });
      return;
    }
    setIsVendorSearching(true);
    try {
      const payload = {
        companyCode: form.companyCode?.value || form.companyCode,
        type: "getVendorByName",
        vendorName: vendorSearch.trim(),
      };
      const response: any = await appRequest(
        "user",
        "masterFinancialApi",
        payload,
      );
      if (response?.status === "success" && Array.isArray(response?.data)) {
        const options = response.data.filter(
          (v: any) => v.vendorCode && v.supplierName,
        );
        setVendorList(options);
        setShowVendorList(options.length > 0);
        if (options.length === 0) {
          toast({
            title: "No vendors found",
            status: "info",
            duration: 3000,
          });
        }
      } else {
        setVendorList([]);
        setShowVendorList(false);
      }
    } catch (error: any) {
      toast({
        title: "Failed to search vendor",
        description: error?.message || "Please try again later.",
        status: "error",
      });
    } finally {
      setIsVendorSearching(false);
    }
  };

  const handleSubmit = async () => {
    try {
      // For create mode, file is required
      if (!isEditMode && !documentFile) {
        toast({
          title: "Missing Fields",
          description: "Please upload a document.",
          status: "error",
          isClosable: true,
        });
        return;
      }

      if (
        !form.companyCode ||
        !form.vendorCode ||
        !form.validateFrom ||
        !form.validateTo ||
        !documentName
      ) {
        toast({
          title: "Missing Fields",
          description: "Please fill all required fields.",
          status: "error",
          isClosable: true,
        });
        return;
      }

      // Convert file to Base64 if provided
      let base64String: string | undefined;
      if (documentFile) {
        const convertToBase64 = (file: File): Promise<string> => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
              const result = reader.result as string;
              const base64 = result.split(",")[1];
              resolve(base64);
            };
            reader.onerror = (error) => reject(error);
          });
        };
        base64String = await convertToBase64(documentFile);
      }

      const payload: any = {
        companyCode: form.companyCode?.value || form.companyCode,
        vendorName: form.vendorName,
        vendorCode: form.vendorCode,
        validateFrom: form.validateFrom,
        validateTo: form.validateTo,
        isActive: form.isActive,
        documentName,
        processName: workflowProcess?.processName,
        processType: workflowProcess?.processType,
      };

      // Only include file if it was uploaded
      if (base64String && documentFile) {
        payload.file = {
          file: base64String,
          filename: documentFile.name,
          type: documentFile.type,
        };
      }

      setSubmitLoading(true);
      let response: any;

      if (isEditMode) {
        // Update mode - pass id in payload for appRequest to extract
        const updatePayload = {
          ...payload,
          id: editData._id,
        };
        response = await appRequest("documentMaster", "update", updatePayload);
      } else {
        // Create mode
        response = await appRequest("documentMaster", "create", payload);
      }

      setSubmitLoading(false);
      if (response?.status !== "success") {
        toast({
          title: "Error",
          description:
            response?.message ||
            `Failed to ${isEditMode ? "update" : "create"} master data`,
          status: "error",
          isClosable: true,
        });
        return;
      }

      getData({});
      toast({
        title: "Success",
        description: `Master data ${isEditMode ? "updated" : "created"} successfully`,
        status: "success",
        isClosable: true,
      });

      onClose();
    } catch (err: any) {
      console.error("Submit Error:", err);
      setSubmitLoading(false);
      toast({
        title: "Error",
        description: err?.message || "Failed to save record",
        status: "error",
        isClosable: true,
      });
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent
        maxW="90vw" // 80% of viewport width
        w="90vw"
        bg="gray.100"
      >
        <DrawerCloseButton />
        <DrawerHeader bg="white">
          {isEditMode ? "Edit Master Data" : "Add Master Data Configuration"}
        </DrawerHeader>

        <DrawerBody p={0}>
          <Grid
            templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
            gap={0}
            minH="calc(100vh - 120px)"
          >
            {/* Preview Section */}
            <Box
              bg="white"
              borderRadius="0"
              p={0}
              borderRight={{ lg: "1px solid #e2e8f0" }}
              minH="100%"
              display="flex"
              flexDirection="column"
            >
              <Box
                flex="1"
                bg="gray.50"
                borderRadius="0"
                p={8}
                border="none"
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                {fetchFileLoader ? (
                  <Flex
                    align="center"
                    justify="center"
                    h="100%"
                    direction="column"
                  >
                    <Spinner size="lg" color="teal.500" mb={4} />
                    <Text color="gray.600">Loading document...</Text>
                  </Flex>
                ) : documentUrl ? (
                  <FileViewer
                    sample={{
                      url: documentUrl,
                      name: documentFile?.name || editData?.documentName,
                      type:
                        documentFile?.type ||
                        editData?.file?.metadata?.contentType ||
                        "application/pdf",
                      file: documentFile,
                      buffer: null,
                    }}
                  />
                ) : (
                  <Flex
                    align="center"
                    justify="center"
                    h="100%"
                    direction="column"
                    color="gray.400"
                  >
                    <ViewIcon w={16} h={16} mb={4} opacity={0.5} />
                    <Text fontSize="md" fontWeight="500">
                      No Document Selected
                    </Text>
                    <Text fontSize="sm" mt={2}>
                      Upload a document in the form below
                    </Text>
                  </Flex>
                )}
              </Box>
            </Box>

            {/* Form Section */}
            <Box bg="white" p={0} overflowY="auto" maxH="calc(100vh - 120px)">
              <Box px={2} py={2}>
                <VStack spacing={2} align="stretch">
                  {/* Company Code Section */}
                  <Box p={6} borderRadius="md" border="1px solid #e2e8f0">
                    <Text
                      fontSize="sm"
                      fontWeight="700"
                      mb={2}
                      color="gray.800"
                      textTransform="uppercase"
                      letterSpacing="0.5px"
                    >
                      Basic Information
                    </Text>
                    <VStack spacing={5} align="stretch">
                      {/* Company Code Row */}
                      <Box>
                        <CustomInput
                          name="companyCode"
                          label="Company Code *"
                          type="select"
                          value={form.companyCode}
                          onChange={(e) => setForm({ ...form, companyCode: e })}
                          options={COMPANY_CODES.map((c) => ({
                            label: c,
                            value: c,
                          }))}
                        />
                      </Box>

                      {/* Vendor Row - Search Input + Vendor Code */}
                      <Box ref={vendorContainerRef}>
                        <Text
                          fontSize="sm"
                          fontWeight="600"
                          mb={3}
                          color="gray.700"
                        >
                          Search Vendor by Name/Code *
                        </Text>
                        <Flex gap={2} align="flex-end" mb={3}>
                          <Box flex="1" position="relative">
                            <Input
                              value={vendorSearch}
                              onChange={handleVendorSearchChange}
                              onFocus={() => {
                                if (
                                  vendorList.length > 0 &&
                                  vendorSearch.length >= 2
                                )
                                  setShowVendorList(true);
                              }}
                              placeholder="Enter vendor name or code..."
                              isDisabled={!form.companyCode}
                              size="md"
                              borderRadius="md"
                            />
                            {isVendorLoading && (
                              <Spinner
                                size="xs"
                                color="blue.500"
                                position="absolute"
                                right={3}
                                top="50%"
                                transform="translateY(-50%)"
                              />
                            )}
                          </Box>
                          <Button
                            onClick={handleVendorSearch}
                            isLoading={isVendorSearching}
                            colorScheme="blue"
                            size="md"
                            isDisabled={
                              !form.companyCode || !vendorSearch.trim()
                            }
                            minW="100px"
                            height="40px"
                          >
                            Search
                          </Button>
                        </Flex>

                        {showVendorList && (
                          <Box
                            bg="white"
                            border="1px solid #cbd5e0"
                            mt={1}
                            borderRadius="md"
                            boxShadow="lg"
                            maxH="240px"
                            overflowY="auto"
                            zIndex={10}
                          >
                            {vendorList.length > 0 ? (
                              vendorList.map((v, i) => (
                                <Box
                                  key={i}
                                  p={3.5}
                                  _hover={{ bg: "blue.50", cursor: "pointer" }}
                                  onClick={() => selectVendor(v)}
                                  borderBottom={
                                    i < vendorList.length - 1
                                      ? "1px solid #eeee"
                                      : "none"
                                  }
                                  transition="background-color 0.2s"
                                >
                                  <Text
                                    fontWeight="600"
                                    fontSize="sm"
                                    color="gray.800"
                                  >
                                    {v.supplierName}
                                  </Text>
                                  <Text fontSize="xs" color="gray.600" mt={1.5}>
                                    Code:{" "}
                                    <Text
                                      as="span"
                                      fontWeight="500"
                                      color="gray.800"
                                    >
                                      {v.vendorCode}
                                    </Text>
                                  </Text>
                                  {v.vendorAddress && (
                                    <Text
                                      fontSize="xs"
                                      color="gray.500"
                                      mt={2.5}
                                      lineHeight="1.5"
                                    >
                                      {v.vendorAddress}
                                    </Text>
                                  )}
                                </Box>
                              ))
                            ) : (
                              <Box p={4} textAlign="center">
                                <Text fontSize="sm" color="gray.500">
                                  No vendors found.
                                </Text>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>

                      {/* Vendor Code and Vendor Name on same row */}
                      <Flex gap={4} align="flex-end">
                        <Box flex="1">
                          <CustomInput
                            name="vendorCode"
                            label="Vendor Code"
                            value={form.vendorCode}
                            type="text"
                            isReadOnly
                          />
                        </Box>
                        <Box flex="1">
                          <Text
                            fontSize="sm"
                            fontWeight="500"
                            mb={2}
                            color="gray.700"
                          >
                            Vendor Name
                          </Text>
                          <Input
                            value={form.vendorName}
                            isReadOnly
                            placeholder="Selected vendor name"
                            size="md"
                            borderRadius="md"
                            bg="gray.50"
                          />
                        </Box>
                      </Flex>
                    </VStack>
                  </Box>

                  {/* Date Range Section */}
                  <Box p={6} borderRadius="md" border="1px solid #e2e8f0">
                    <Text
                      fontSize="sm"
                      fontWeight="700"
                      mb={3}
                      color="gray.800"
                      textTransform="uppercase"
                      letterSpacing="0.5px"
                    >
                      Validity Period
                    </Text>
                    <Flex gap={4} align="flex-end">
                      <Box flex="1">
                        <CustomInput
                          name="validateFrom"
                          label="Valid From *"
                          type="date"
                          value={form.validateFrom}
                          onChange={handleInputChange}
                        />
                      </Box>

                      <Box flex="1">
                        <CustomInput
                          name="validateTo"
                          label="Valid To *"
                          type="date"
                          value={form.validateTo}
                          onChange={handleInputChange}
                        />
                      </Box>
                    </Flex>
                  </Box>

                  {/* Document Section */}
                  <Box p={6} borderRadius="md" border="1px solid #e2e8f0">
                    <Text
                      fontSize="sm"
                      fontWeight="700"
                      mb={5}
                      color="gray.800"
                      textTransform="uppercase"
                      letterSpacing="0.5px"
                    >
                      Supporting Document
                    </Text>
                    <VStack spacing={5} align="stretch">
                      <Box>
                        <Text
                          fontSize="sm"
                          fontWeight="500"
                          mb={2}
                          color="gray.700"
                        >
                          Upload File *
                        </Text>
                        <Input
                          type="file"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              const file = e.target.files[0];
                              setDocumentFile(file);
                              setDocumentUrl(URL.createObjectURL(file));
                              // Auto-populate document name with file name
                              setDocumentName(file.name);
                            }
                          }}
                          size="md"
                          borderRadius="md"
                          pt={1}
                        />
                      </Box>

                      {documentFile && (
                        <Box
                          bg="green.50"
                          p={3.5}
                          borderRadius="md"
                          border="1px solid #9ae6b4"
                        >
                          <Flex align="center" gap={2.5}>
                            <Box
                              w={2.5}
                              h={2.5}
                              bg="green.500"
                              borderRadius="full"
                            />
                            <VStack align="flex-start" spacing={0.5}>
                              <Text
                                fontSize="xs"
                                fontWeight="500"
                                color="green.600"
                              >
                                File:
                              </Text>
                              <Text
                                fontSize="sm"
                                fontWeight="500"
                                color="green.800"
                              >
                                {documentFile.name}
                              </Text>
                            </VStack>
                          </Flex>
                        </Box>
                      )}

                      <Flex gap={4} align="flex-end">
                        <Box flex="1">
                          <Text
                            fontSize="sm"
                            fontWeight="500"
                            mb={2}
                            color="gray.700"
                          >
                            Document Name *
                            {documentFile && (
                              <Text
                                as="span"
                                fontSize="xs"
                                fontWeight="400"
                                color="gray.500"
                                ml={2}
                              >
                                (File: {documentFile.name})
                              </Text>
                            )}
                          </Text>
                          <Select
                            placeholder="Select document type"
                            value={documentName}
                            onChange={(e) => setDocumentName(e.target.value)}
                            size="md"
                            borderRadius="md"
                            borderColor={
                              documentName === documentFile?.name
                                ? "green.300"
                                : undefined
                            }
                            _focus={
                              documentName === documentFile?.name
                                ? {
                                  borderColor: "green.400",
                                  boxShadow: "0 0 0 1px var(--chakra-colors-green-400)",
                                }
                                : undefined
                            }
                          >
                            <option value="agreement">Agreement</option>
                            <option value="tax">Tax Document</option>
                            <option value="other">Supporting</option>
                          </Select>

                        </Box>
                        <Box display="flex" alignItems="flex-end" pb={2}>
                          <Switch
                            isChecked={form.isActive}
                            onChange={(e) =>
                              setForm({ ...form, isActive: e.target.checked })
                            }
                            colorScheme="green"
                            size="lg"
                          />
                        </Box>
                      </Flex>
                    </VStack>
                  </Box>
                </VStack>
              </Box>
            </Box>
          </Grid>
        </DrawerBody>

        <DrawerFooter bg="white">
          <Button mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            isLoading={submitLoading}
            colorScheme="teal"
            onClick={handleSubmit}
          >
            {isEditMode ? "Update Master Data" : "Save Master Data"}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default MasterDataDrawer;